import styled, { css } from 'styled-components';
import { Box, Card, Heading, Link, Stack, Text } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors, spaces } from '@99designs/emu/settings';
import { IconRight1 } from '@99designs/icons';
import { FeaturesList } from '@99designs/marketing-ui';
import { trackLink } from '../../lib/tracking';
import { CollaborationMethod as CollaborationMethodValue } from '../../types';
import type { CollaborationContent } from './collaborationMethods';

export interface CollaborationMethodProps extends CollaborationContent {
    type: CollaborationMethodValue;
    crossPlatformTrackingParams?: string;
    productName?: string;
}

const StyledCard = styled(Card)<{ type: CollaborationMethodValue }>`
    position: relative;
    background-color: ${colors.grey050};
    background-repeat: no-repeat;
    background-position: ${({ type }) =>
        type === CollaborationMethodValue.Care
            ? `bottom ${spaces['1bu']} right ${spaces['1bu']}`
            : 'bottom right'};
    background-size: ${({ type }) =>
        type === CollaborationMethodValue.Care ? '70px' : '136px 136px'};

    ${({ type }) =>
        type &&
        css`
            background-image: url('https://99designs-start-static.imgix.net/product-details/collaboration-methods/${type}.png');
        `}

    ${mediaQuery.only('small')} {
        background-color: transparent;
        padding: 0 0 ${spaces['3bu']};
        background-position: bottom right;

        ${({ type }) =>
            [CollaborationMethodValue.Contest, CollaborationMethodValue.ContestBundle].includes(
                type
            ) &&
            css`
                background-image: url('https://99designs-start-static.imgix.net/product-details/collaboration-methods/${type}_mobile.png');
                padding-bottom: ${spaces['4bu']};
            `}
    }
`;

export function CollaborationMethod({
    title,
    description,
    inclusions,
    link,
    type,
    crossPlatformTrackingParams,
    productName = '',
}: CollaborationMethodProps) {
    return (
        <>
            <Box paddingBottom="1bu">
                <Heading appearanceLevel={4} accessibilityLevel={2}>
                    {__('Collaboration method')}
                </Heading>
            </Box>
            <StyledCard appearance="filled" type={type}>
                <Stack space="0.5bu">
                    <Heading appearanceLevel={5} accessibilityLevel={3}>
                        {title}
                    </Heading>
                    <Text>{description}</Text>
                    <Box width={['95%', '85%', '80%', '80%']}>
                        <FeaturesList features={inclusions} />
                    </Box>

                    {link && (
                        <Link
                            href={`${link.href}${
                                crossPlatformTrackingParams ? `?${crossPlatformTrackingParams}` : ''
                            }`}
                            onClick={(event) =>
                                trackLink(
                                    event,
                                    'Find out more link on Product details page clicked',
                                    'Navigation Clicked',
                                    {
                                        category: 'Product Page',
                                        label: 'Content:Product Detail',
                                        eventDetail: `/design/${productName};${link.href};Product Detail;Find out more`,
                                        navigationDetail: 'Find out more',
                                        pageName: `${productName}:Product Page`,
                                        pageStage: 'Discover',
                                        pageSection: 'Product Page',
                                        pageDept: 'Design Services',
                                    }
                                )
                            }
                            openInNewTab
                        >
                            {__('Find out more')}
                            <Box as="span" marginLeft="0.25bu">
                                <IconRight1 />
                            </Box>
                        </Link>
                    )}
                </Stack>
            </StyledCard>
        </>
    );
}
